import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthState, CompanyPayload, CompanyState, TokensPayload, UserPayload } from "@redux/features/auth/types";
import { Integration } from "@/types/general";
import { AppAsyncThunk, RootState } from "@redux/hooks";
import { companiesApi } from "@redux/features/companies/companiesApi";


export const authSlice = createSlice({
    name: "auth",
    initialState: {} as AuthState,
    reducers: {
        authenticate: (state, action: PayloadAction<TokensPayload>) => {
            const tokens = action.payload;
            state.tokens = tokens;

            state.isAuthenticated = true;

            localStorage.setItem("sanctum", tokens.access);
        },

        logout(state) {
            state.tokens = undefined;
            state.user = undefined;
            state.isAuthenticated = false;

            localStorage.setItem("sanctum", "");
        },

        setUser(state, action: PayloadAction<UserPayload>) {
            state.user = {
                ...action.payload,
                company: action.payload.company && {
                    ...action.payload.company,
                    integrations: action.payload.company?.integrations.reduce((acc, integration) => {
                        acc[integration.slug] = integration;
                        return acc;
                    }, {} as Record<Integration.ActiveState.Root["slug"], Integration.ActiveState.Root>)
                }
            };
        },

        setRole(state, action: PayloadAction<UserPayload["role"]>) {
            if (state.user) {
                state.user.role = action.payload;
            } else {
                console.warn("Trying to set permissions for an unauthenticated user!");
            }
        },

        setAccessToken(state, action: PayloadAction<string>) {
            if (state.tokens) {
                state.tokens.access = action.payload;
            } else {
                console.warn("Trying to set an access token for an unauthenticated user!");
            }
        },

        setCompany(state, action: PayloadAction<CompanyPayload | undefined>) {
            if (state.user) {
                if (action.payload === undefined) {
                    state.user.company = undefined;
                } else {
                    state.user.company = {
                        ...action.payload,
                        integrations: action.payload.integrations.reduce((acc, integration) => {
                            acc[integration.slug] = integration;
                            return acc;
                        }, {} as Record<Integration.ActiveState.Root["slug"], Integration.ActiveState.Root>)
                    };
                }
            } else {
                console.warn("Trying to set a company for an unauthenticated user!");
            }
        },

        resetCompanyToState(state, action: PayloadAction<CompanyState>) {
            if (state.user) {
                state.user.company = action.payload;
            }
        },

        setIntegrations(state, action: PayloadAction<CompanyPayload["integrations"]>) {
            if (state.user?.company) {
                state.user.company.integrations = action.payload.reduce((acc, integration) => {
                    acc[integration.slug] = integration;
                    return acc;
                }, {} as Record<Integration.ActiveState.Root["slug"], Integration.ActiveState.Root>);
            } else {
                console.warn("Trying to set integrations for an unauthenticated user!");
            }
        },

        enableIntegration(state, action: PayloadAction<Integration.ActiveState.Root>) {
            if (state.user?.company) {
                state.user.company.integrations![action.payload.slug] = action.payload;
            } else {
                console.warn("Trying to set an integration for an unauthenticated user!");
            }
        },
        disableIntegration(state, action: PayloadAction<Integration.ActiveState.Root["slug"]>) {
            if (state.user?.company) {
                delete state.user.company.integrations![action.payload];
            } else {
                console.warn("Trying to disable an integration for an unauthenticated user!");
            }
        }
    }
});

export const isSystemIntegratedSelector = (slug: Integration.ActiveState.Root["slug"]) => (state: RootState) => {
    return slug in (state.auth.user?.company?.integrations ?? {});
};

export function switchCompany(company_id: number): AppAsyncThunk {
    return async (dispatch, getState) => {
        const activeCompany = getState().auth.user?.company;

        if (company_id === activeCompany?.id) return;

        const previousState = activeCompany;

        let companyQuery;
        try {
            companyQuery = dispatch(companiesApi.endpoints.getCompany.initiate(company_id));

            const companyData = await companyQuery.unwrap();

            await dispatch(companiesApi.endpoints.updateActiveCompany.initiate(company_id)).unwrap();

            dispatch(setCompany(companyData));
        } catch (e) {
            dispatch(resetCompanyToState(previousState as NonNullable<typeof previousState>));

            throw e;
        } finally {
            companyQuery?.unsubscribe();
        }
    };
}

export const getPowerOfficeIntegrationSettingsSelector = (state: RootState) => state.auth.user?.company?.integrations?.poweroffice as Integration.ActiveState.PowerOfficeGO;
export const getTripletexIntegrationSettingsSelector = (state: RootState) => state.auth.user?.company?.integrations?.tripletex as Integration.ActiveState.Tripletex;

// Action creators are generated for each case reducer function
export const {
    authenticate,
    logout,
    setRole,
    setCompany,
    resetCompanyToState,
    setUser,
    setAccessToken,
    setIntegrations,
    enableIntegration,
    disableIntegration
} = authSlice.actions;

export default authSlice.reducer;